import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery, useUpdateBrokerMutation } from '@/features/user/userApi';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { storage } from '@/utils/firebase';
import { Avatar, Box, CircularProgress, FormHelperText, FormLabel, Stack } from '@mui/material';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { STORAGE_PURPOSES, brokerStoragePath } from '@/utils/storagePaths';
import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const MAX_WIDTH = 800;
const MAX_HEIGHT = 200;

const BrokerageLogo = ({
	brokerGroupId,
	canEdit = true,
	onUploadingChange,
}) => {
	const { values, setFieldValue } = useFormikContext();
	const dispatch = useDispatch();
	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: currentUser } = useGetUserDetailsQuery({ email }, { skip: !email });
	const [isLogoUploading, setIsLogoUploading] = useState(false);
	const [updateBroker] = useUpdateBrokerMutation();

	const setUploading = (nextValue) => {
		setIsLogoUploading(nextValue);
		onUploadingChange?.(nextValue);
	};

	const handleLogoUpload = async (event) => {
		if (!canEdit || !brokerGroupId) return;
		setUploading(true);
		const file = event.target.files?.[0];
		if (!file) {
			setUploading(false);
			return;
		}

		const fileExtension = file.name.split('.').pop().toLowerCase();
		if (fileExtension !== 'jpg' && fileExtension !== 'jpeg' && fileExtension !== 'png') {
			dispatch(showToast({ message: 'Please upload a .jpg or .png file', error: true }));
			setUploading(false);
			return;
		}

		try {
			const img = new Image();
			const reader = new FileReader();

			reader.onload = (e) => {
				img.src = e.target.result;
			};

			img.onload = async () => {
				const canvas = document.createElement('canvas');
				let { width, height } = img;
				// Compute the scale factor to maintain aspect ratio
				const widthRatio = MAX_WIDTH / width;
				const heightRatio = MAX_HEIGHT / height;
				const scale = Math.min(1, widthRatio, heightRatio); // Don't scale up

				width *= scale;
				height *= scale;

				canvas.width = width;
				canvas.height = height;

				const ctx = canvas.getContext('2d');
				ctx.drawImage(img, 0, 0, width, height);

				canvas.toBlob(async (blob) => {
					if (!blob) {
						dispatch(showToast({ message: 'Failed to process image', error: true }));
						setUploading(false);
						return;
					}
					// Preview as base64 (optional)
					const previewUrl = URL.createObjectURL(blob);
					setFieldValue('fullLogo', previewUrl);

					// Upload to Firebase
					const storageRef = ref(
						storage,
						brokerStoragePath(brokerGroupId, STORAGE_PURPOSES.logo, 'logo.png'),
					);
					await uploadBytes(storageRef, blob);

					const downloadUrl = await getDownloadURL(storageRef);
					setFieldValue('fullLogo', downloadUrl);

					await updateBroker({
						docId: brokerGroupId,
						properties: { fullLogo: downloadUrl },
					}).unwrap();

					dispatch(
						showToast({
							message: 'Logo uploaded successfully!',
							success: true,
						}),
					);
					setUploading(false);
				}, 'image/png');
			};

			reader.readAsDataURL(file);
		} catch (e) {
			console.log('Error with image', e);
			dispatch(showToast({ message: 'Failed to process image', error: true }));
			setUploading(false);
		}
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
			}}
		>
			<Box sx={{ position: 'relative', width: '100%', maxWidth: 500, height: 160 }}>
				<label
					style={{
						position: 'relative',
						display: 'block',
						width: '100%',
						height: '100%',
						cursor: canEdit ? 'pointer' : 'default',
					}}
				>
					<Avatar
						src={values.fullLogo || ''}
						alt='Broker Logo'
						variant='rounded'
						sx={{
							width: '100%',
							height: '100%',
							borderRadius: 2,
							backgroundColor: '#ffffff',
							objectFit: 'cover',
						}}
					>
						{!values.fullLogo && 'Logo'}
					</Avatar>

					{canEdit && !isLogoUploading && (
						<input
							type='file'
							hidden
							accept='image/png, image/jpeg'
							onChange={handleLogoUpload}
						/>
					)}

					{canEdit && (
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								borderRadius: 2,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'backgroundColor 0.3s ease',
								...(!isLogoUploading && { cursor: 'pointer' }),
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.52)',
								},
								color: 'white',
							}}
						>
							{isLogoUploading ? (
								<CircularProgress
									color='inherit'
									sx={{ opacity: 1, zIndex: 999 }}
								/>
							) : (
								<Stack
									direction='column'
									spacing={1}
									justifyContent='center'
									alignItems='center'
									sx={{
										width: '100%',
										height: '100%',
										opacity: 0,
										transition: 'opacity 0.3s ease',
										'&:hover': {
											opacity: 1,
										},
									}}
								>
									<CloudUploadIcon fontSize='large' sx={{ color: 'white' }} />
									<FormLabel sx={{ color: 'white' }}>5mb max file size</FormLabel>
								</Stack>
							)}
						</Box>
					)}
				</label>
			</Box>
			<FormHelperText>{'5mb max file size. Recommended aspect ratio 4:1.'}</FormHelperText>
		</Box>
	);
};

export default BrokerageLogo;
